import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Pricing = () => {
  const [searchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [activeService, setActiveService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, packagesRes] = await Promise.all([
          fetch(`${API}/services`),
          fetch(`${API}/packages`)
        ]);
        const servicesData = await servicesRes.json();
        const packagesData = await packagesRes.json();
        setServices(servicesData);
        setPackages(packagesData);
        
        const serviceParam = searchParams.get('service');
        if (serviceParam) {
          setActiveService(serviceParam);
        } else if (servicesData.length > 0) {
          setActiveService(servicesData[0].service_id);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [searchParams]);

  const filteredPackages = packages.filter(p => p.service_id === activeService);
  const currentService = services.find(s => s.service_id === activeService);

  const tierOrder = { basic: 0, standard: 1, premium: 2 };
  const sortedPackages = [...filteredPackages].sort((a, b) => tierOrder[a.tier] - tierOrder[b.tier]);

  const getTierStyles = (tier) => {
    switch (tier) {
      case 'premium':
        return 'border-2 border-foreground relative';
      case 'standard':
        return 'border border-border';
      default:
        return 'border border-border';
    }
  };

  return (
    <main className="pt-24 pb-20 min-h-screen bg-background" data-testid="pricing-page">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Volver al inicio
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="text-sm uppercase tracking-widest text-muted-foreground block mb-4">
            Tarifas
          </span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mb-6">
            Paquetes y Precios
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Elige el paquete que mejor se adapte a tus necesidades. 
            Todos incluyen edición profesional y entrega digital.
          </p>
        </motion.div>

        {/* Service Tabs */}
        {!loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-wrap justify-center gap-2 mb-12"
          >
            {services.map((service) => (
              <button
                key={service.service_id}
                onClick={() => setActiveService(service.service_id)}
                className={`px-6 py-3 text-xs uppercase tracking-widest transition-colors ${
                  activeService === service.service_id
                    ? 'bg-foreground text-background'
                    : 'bg-transparent text-muted-foreground hover:text-foreground border border-border'
                }`}
                data-testid={`pricing-tab-${service.service_id}`}
              >
                {service.name}
              </button>
            ))}
          </motion.div>
        )}

        {/* Service Description */}
        {currentService && (
          <motion.div
            key={currentService.service_id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center mb-12"
          >
            <p className="text-muted-foreground max-w-xl mx-auto">{currentService.description}</p>
          </motion.div>
        )}

        {/* Packages Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-96 bg-muted animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {sortedPackages.map((pkg, index) => (
              <motion.div
                key={pkg.package_id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`bg-card p-8 ${getTierStyles(pkg.tier)}`}
                data-testid={`package-${pkg.package_id}`}
              >
                {pkg.tier === 'premium' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-foreground text-background px-4 py-1 text-xs uppercase tracking-widest">
                    Recomendado
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                    {pkg.tier === 'basic' ? 'Básico' : pkg.tier === 'standard' ? 'Estándar' : 'Premium'}
                  </span>
                  <h3 className="font-serif text-2xl mb-2">{pkg.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{pkg.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-serif text-5xl">{pkg.price}</span>
                    <span className="text-muted-foreground">€</span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full rounded-none uppercase tracking-widest text-xs py-3 ${
                    pkg.tier === 'premium'
                      ? 'bg-foreground text-background hover:bg-foreground/90'
                      : 'bg-transparent border border-foreground text-foreground hover:bg-foreground hover:text-background'
                  } transition-colors duration-300`}
                  onClick={() => {
                    const element = document.getElementById('contacto');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      window.location.href = '/#contacto';
                    }
                  }}
                  data-testid={`book-${pkg.package_id}`}
                >
                  Reservar
                </Button>
              </motion.div>
            ))}
          </div>
        )}

        {/* Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center mt-16 text-sm text-muted-foreground"
        >
          <p>
            * Los precios no incluyen IVA. Para proyectos personalizados o necesidades especiales, 
            <a href="/#contacto" className="text-foreground underline-offset-4 hover:underline ml-1">
              contáctame
            </a>.
          </p>
        </motion.div>
      </div>
    </main>
  );
};

export default Pricing;
